import { Alert } from '@designsforhealth/dfh-react-components'
import React, { useCallback, useContext, useEffect, useReducer, useRef } from 'react'
import styled from 'styled-components'

import ClickWrapModalActions from '../ClickWrapModalActions'
import ClickWrapModalDataProviderActions from '../ClickWrapModalDataProviderActions'
import { ClickWrapContext, UserDataProviderContext } from '../context'
import { ClickWrapAdditionalActionsComponent, ClickWrapContentComponent } from '../types'
import * as Styled from './styled'

interface ContentState {
  additionalActionsPending: boolean
  contentKey: string
  contentReviewPending: boolean
}

type ContentReducerAction =
  | {
      type: 'additionalActionsChanged'
      isComplete: boolean
    }
  | {
      type: 'contentKeyChanged' | 'scrollComplete'
    }

const saveErrorMessage =
  'An error occurred while saving. Please try again and contact Customer Experience if the error persists.'

const useContentStateReducer = ({
  AdditionalActions,
  Content,
}: {
  AdditionalActions?: ClickWrapAdditionalActionsComponent | null
  Content: ClickWrapContentComponent
}) =>
  useReducer(
    (prevState: ContentState, action: ContentReducerAction) => {
      switch (action.type) {
        case 'additionalActionsChanged':
          return {
            ...prevState,
            additionalActionsPending: !action.isComplete,
          }
        case 'contentKeyChanged':
          return {
            ...prevState,
            additionalActionsPending: !!AdditionalActions,
            contentKey: Content.documentKey,
            contentReviewPending: true,
          }
        case 'scrollComplete':
          return prevState.contentReviewPending
            ? { ...prevState, contentReviewPending: false }
            : prevState
        default:
          console.error(`Invalid action: ${JSON.stringify(action)}`)
          return prevState
      }
    },
    {
      additionalActionsPending: !!AdditionalActions,
      contentKey: Content.documentKey,
      contentReviewPending: true,
    },
    undefined,
  )

export interface ClickWrapModalContentProps {
  className?: string
  acceptError: unknown
  onAccept: (e: MouseEvent) => void
  setUserInput: (input: Record<string, unknown>) => void
  Content: ClickWrapContentComponent
  AdditionalActions?: ClickWrapAdditionalActionsComponent | null
}

const ClickWrapModalContent: React.FC<ClickWrapModalContentProps> = ({
  className,
  acceptError,
  onAccept,
  setUserInput,
  Content,
  AdditionalActions,
}) => {
  const [, clickWrapDispatch] = useContext(ClickWrapContext)
  const { FormComponent: DataProviderFormComponent, ...dataProvider } =
    useContext(UserDataProviderContext)

  const [contentState, contentDispatch] = useContentStateReducer({ AdditionalActions, Content })

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!dataProvider.isActive) {
      const { current: scrollContainer } = scrollContainerRef
      if (scrollContainer !== null) {
        scrollContainer.focus()
      }
    }
  }, [dataProvider.isActive])

  const acceptAction = useCallback(
    async (e) => {
      try {
        clickWrapDispatch({ type: 'acceptPending' })
        await onAccept(e)
      } catch (error) {
        // TODO show error in modal
        console.error(error)
      }
    },
    [clickWrapDispatch, onAccept],
  )

  useEffect(() => {
    const isChangedContent = contentState.contentKey !== Content.documentKey
    if (isChangedContent) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0
      }
      contentDispatch({ type: 'contentKeyChanged' })
      clickWrapDispatch({ type: 'contentChanged' })
    }
  }, [Content.documentKey, clickWrapDispatch, contentDispatch, contentState.contentKey])

  const handleScroll = useCallback(() => {
    if (
      contentRef.current &&
      scrollContainerRef.current &&
      contentRef.current.clientHeight - scrollContainerRef.current.clientHeight > 0 &&
      scrollContainerRef.current.scrollTop >=
        contentRef.current.clientHeight - scrollContainerRef.current.clientHeight
    ) {
      contentDispatch({ type: 'scrollComplete' })
    }
  }, [contentDispatch])

  const additionalActionsChanged = useCallback(
    ({ isComplete }) => {
      contentDispatch({ type: 'additionalActionsChanged', isComplete })
    },
    [contentDispatch],
  )

  return (
    <div className={className}>
      <Styled.ScrollContainer tabIndex={0} ref={scrollContainerRef} onScroll={handleScroll}>
        {DataProviderFormComponent != null && (
          <Styled.ProviderFormContainer>
            <DataProviderFormComponent
              disabled={!dataProvider.isActive}
              dataProviderError={dataProvider.error}
            />
            {!dataProvider.isActive && (
              <Styled.ProviderFormEditButton onClick={dataProvider.editData}>
                Click To Edit
              </Styled.ProviderFormEditButton>
            )}
          </Styled.ProviderFormContainer>
        )}
        {!dataProvider.isActive && (
          <>
            <div ref={contentRef}>
              <Content />
            </div>
            {AdditionalActions ? (
              <AdditionalActions
                disabled={contentState.contentReviewPending}
                onChanged={additionalActionsChanged}
                setUserInput={setUserInput}
              />
            ) : null}
          </>
        )}
      </Styled.ScrollContainer>
      {dataProvider.isActive ? (
        <>
          {dataProvider.error && <Alert type="error">{saveErrorMessage}</Alert>}
          <ClickWrapModalDataProviderActions
            isSubmitting={dataProvider.isSubmitting}
            submitDisabled={!dataProvider.isFormValid}
            onSubmit={dataProvider.onSubmit}
          />
        </>
      ) : (
        <>
          {acceptError && <Alert type="error">{saveErrorMessage}</Alert>}
          <ClickWrapModalActions
            acceptAction={acceptAction}
            acceptDisabled={
              contentState.contentReviewPending || contentState.additionalActionsPending
            }
            printContent={() => scrollContainerRef.current}
          />
        </>
      )}
    </div>
  )
}

const StyledClickWrapModalContent = styled(ClickWrapModalContent)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

export default StyledClickWrapModalContent
